"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.CalendarPicker = CalendarPicker;
exports.chunk = chunk;
exports.inRange = inRange;
exports.isDateRange = isDateRange;
exports.isSameDay = isSameDay;
var _lib = require("@a1s/lib");
var _dateFns = require("date-fns");
var _polished = require("polished");
var _react = _interopRequireWildcard(require("react"));
var _styledComponents = _interopRequireDefault(require("styled-components"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
//
// Typescript types
// -------------------------------------------------------------------------------------------------
//
// Styled components
// -------------------------------------------------------------------------------------------------
const Base = _styledComponents.default.div`
  font-size: ${(0, _polished.rem)(12)};
`;
const Calendar = _styledComponents.default.table`
  border-collapse: collapse;
  width: 100%;

  & td {
    cursor: pointer;
    color: ${p => p.theme.colors.dimGrey};
    font-size: ${(0, _polished.rem)(11)};
    height: ${(0, _polished.rem)(40)};
    text-align: center;
    vertical-align: middle;
  }

  & th {
    font-size: ${(0, _polished.rem)(11)};
    text-transform: uppercase;
  }
`;
const DateButton = _styledComponents.default.button`
  background-color: transparent;
  border: 0;
  height: 100%;
  width: 100%;

  *:hover > & {
    background-color: ${p => p.theme.colors.antiFlashWhite};
  }

  &:active {
    color: ${p => p.theme.colors.raisinBlack};
  }

  *[data-within='false'] > & {
    color: ${p => p.theme.colors.ashGrey};
  }

  &[aria-current='date'] {
    color: ${p => p.theme.colors.blueDeFrance};
    font-weight: 800;
  }

  &[aria-selected='true'] {
    background-color: ${p => p.theme.colors.deepSkyBlue};
    color: ${p => p.theme.colors.white};
  }

  &[data-highlighted='true'] {
    background-color: ${p => p.theme.colors.gainsboro};
    color: ${p => p.theme.colors.independence};
  }
`;
const Header = _styledComponents.default.div`
  align-items: center;
  display: flex;
  margin-bottom: ${p => (0, _polished.rem)(p.theme.spacing[1])};
`;
const MonthName = _styledComponents.default.div`
  color: ${p => p.theme.colors.independence};
  flex-grow: 2;
  text-align: center;
`;
const NavButton = _styledComponents.default.button`
  background-color: transparent;
  border: 0;
  color: ${p => p.theme.colors.dimGrey};
  font-family: inherit;
  font-size: ${(0, _polished.rem)(14)};
  height: ${(0, _polished.rem)(44)};
  width: ${(0, _polished.rem)(44)};

  &:hover {
    background-color: ${p => p.theme.colors.gainsboro};
    cursor: pointer;
  }
`;

//
// Main component
// -------------------------------------------------------------------------------------------------
/**
 * A basic calendar display/picker component. This is probably not the component that you want to use, rather, this is
 * a lower-level component for building calendar displays and selectors. Instead you should check the `DateSelect` or
 * `DateRangeSelect` components.
 */
function CalendarPicker({
  maxDate,
  minDate,
  selected,
  ...props
}) {
  const gridIDRef = (0, _react.useRef)((0, _lib.random)());
  const [visibleMonth, setVisbleMonth] = (0, _react.useState)(() => {
    if (!selected) return new Date();
    if (isDateRange(selected)) return selected[0];
    return selected;
  });
  function handleNextMonthClick() {
    const date = (0, _dateFns.addMonths)(visibleMonth, 1);
    if (maxDate && (0, _dateFns.isAfter)(date, maxDate)) return;
    setVisbleMonth(date);
  }
  function handleNextYearClick() {
    const date = (0, _dateFns.addYears)(visibleMonth, 1);
    if (maxDate && (0, _dateFns.isAfter)(date, maxDate)) return;
    setVisbleMonth(date);
  }
  function handlePrevMonthClick() {
    const date = (0, _dateFns.addMonths)(visibleMonth, -1);
    if (minDate && (0, _dateFns.isBefore)(date, minDate)) return;
    setVisbleMonth(date);
  }
  function handlePrevYearClick() {
    const date = (0, _dateFns.addYears)(visibleMonth, -1);
    if (minDate && (0, _dateFns.isBefore)(date, minDate)) return;
    setVisbleMonth(date);
  }
  return /*#__PURE__*/_react.default.createElement(Base, null, /*#__PURE__*/_react.default.createElement(Header, null, /*#__PURE__*/_react.default.createElement(NavButton, {
    "aria-label": "Previous year",
    onClick: handlePrevYearClick,
    type: "button"
  }, "\xAB"), /*#__PURE__*/_react.default.createElement(NavButton, {
    "aria-label": "Previous month",
    onClick: handlePrevMonthClick,
    type: "button"
  }, "\u2039"), /*#__PURE__*/_react.default.createElement(MonthName, {
    "aria-live": "polite",
    id: gridIDRef.current
  }, (0, _dateFns.format)(visibleMonth, 'LLLL yyyy')), /*#__PURE__*/_react.default.createElement(NavButton, {
    "aria-label": "Next month",
    onClick: handleNextMonthClick,
    type: "button"
  }, "\u203A"), /*#__PURE__*/_react.default.createElement(NavButton, {
    "aria-label": "Next year",
    onClick: handleNextYearClick,
    type: "button"
  }, "\xBB")), /*#__PURE__*/_react.default.createElement(Calendar, {
    "aria-labelledby": gridIDRef.current,
    role: "grid"
  }, /*#__PURE__*/_react.default.createElement(WeeknameRows, null), /*#__PURE__*/_react.default.createElement(CalendarRows, _extends({}, props, {
    maxDate: maxDate,
    minDate: minDate,
    month: visibleMonth,
    selected: selected
  }))));
}

//
// Private components
// -------------------------------------------------------------------------------------------------

function CalendarRows({
  highlighted,
  maxDate,
  minDate,
  month,
  onDateHoverIn,
  onDateHoverOut,
  onDatePress,
  selected
}) {
  // Pre-calculate some values, since we'll be iterating over things a few times.
  const daysInMonth = (0, _dateFns.getDaysInMonth)(month);
  const endDate = (0, _dateFns.endOfMonth)(month);
  const startDate = (0, _dateFns.startOfMonth)(month);

  // Create an array of days from the previous month to pad the first week of the calendar.
  const startPadding = [...new Array((0, _dateFns.getISODay)(startDate) - 1)].map((_, i) => 0 - 1 - i);
  // Create an array of day for the next month to pad the last week of the calendar with.
  const endPadding = [...new Array(7 - (0, _dateFns.getISODay)(endDate))].map((_, i) => daysInMonth + i);
  // Create an array of all of the days of month
  const monthDays = [...new Array(daysInMonth)].map((_, i) => i);

  // Create an array with all of the dates (including the padding we just created).
  const days = [...startPadding, ...monthDays, ...endPadding];
  // Place all of our days into groups of seven, representing weeks.
  const weeks = [...chunk(days, 7)];
  return /*#__PURE__*/_react.default.createElement("tbody", null, weeks.map(week => {
    const iso = (0, _dateFns.format)((0, _dateFns.addDays)(startDate, week[0]), "yyyy-'W'I");
    return /*#__PURE__*/_react.default.createElement("tr", {
      "data-week": iso,
      key: iso
    }, week.map(day => {
      const date = (0, _dateFns.addDays)(startDate, day);
      const isDisabled = minDate && (0, _dateFns.isBefore)(date, minDate) || maxDate && (0, _dateFns.isAfter)(date, maxDate);
      const iso = (0, _dateFns.format)(date, 'yyyy-MM-dd');
      function handleClick() {
        if (onDatePress) onDatePress(date);
      }
      function handleMouseOut() {
        if (onDateHoverOut) onDateHoverOut(date);
      }
      function handleMouseOver() {
        if (onDateHoverIn) onDateHoverIn(date);
      }
      return /*#__PURE__*/_react.default.createElement("td", {
        "data-date": iso,
        "data-within": (0, _dateFns.isSameMonth)(date, month),
        key: iso
      }, /*#__PURE__*/_react.default.createElement(DateButton, {
        "aria-current": (0, _dateFns.isToday)(date) && 'date',
        "aria-selected": inRange(date, selected),
        "data-highlighted": inRange(date, highlighted),
        disabled: isDisabled,
        onBlur: () => void 0,
        onClick: handleClick,
        onFocus: () => void 0,
        onMouseOut: handleMouseOut,
        onMouseOver: handleMouseOver,
        tabIndex: -1,
        type: "button"
      }, (0, _dateFns.format)(date, 'd')));
    }));
  }));
}
function WeeknameRows() {
  return /*#__PURE__*/_react.default.createElement("thead", null, /*#__PURE__*/_react.default.createElement("tr", null, /*#__PURE__*/_react.default.createElement("th", {
    scope: "col",
    abbr: "Monday"
  }, "Mon"), /*#__PURE__*/_react.default.createElement("th", {
    scope: "col",
    abbr: "Tuesday"
  }, "Tue"), /*#__PURE__*/_react.default.createElement("th", {
    scope: "col",
    abbr: "Wednesday"
  }, "Wed"), /*#__PURE__*/_react.default.createElement("th", {
    scope: "col",
    abbr: "Thursday"
  }, "Thu"), /*#__PURE__*/_react.default.createElement("th", {
    scope: "col",
    abbr: "Friday"
  }, "Fri"), /*#__PURE__*/_react.default.createElement("th", {
    scope: "col",
    abbr: "Saturday"
  }, "Sat"), /*#__PURE__*/_react.default.createElement("th", {
    scope: "col",
    abbr: "Sunday"
  }, "Sun")));
}

//
// Private functions
// -------------------------------------------------------------------------------------------------

/**
 * Helper generation function putting elements into groups of a certain size.
 */
function* chunk(array, size) {
  for (let i = 0; i < array.length; i += size) {
    yield array.slice(i, i + size);
  }
}

/**
 * Returns true of the given argument is a `DateRange`.
 */
function isDateRange(arg) {
  return Array.isArray(arg);
}

/**
 * Returns true if the given `Date` is in within the given `DateRange`
 */
function inRange(date, range) {
  if (!range) return false;
  if (!isDateRange(range)) return isSameDay(range, date);
  const earliest = (0, _dateFns.min)(range);
  const numDays = Math.abs((0, _dateFns.differenceInDays)(range[0], range[1]));
  return [...new Array(numDays)].map((_, i) => (0, _dateFns.addDays)(earliest, i)).some(d => isSameDay(d, date));
}

/**
 * Returns true if the two dates are the same day.
 */
function isSameDay(left, right) {
  return (0, _dateFns.isEqual)((0, _dateFns.startOfDay)(left), (0, _dateFns.startOfDay)(right));
}